
import { canMoveTo, rotateBlock } from './gameState';
import { MOBILE_CONFIG } from '../../utils/mobile/constants';

export function setupInputHandlers(scene) {
  // Keyboard controls
  scene.input.keyboard.on('keydown-LEFT', () => moveLeft(scene));
  scene.input.keyboard.on('keydown-RIGHT', () => moveRight(scene));
  scene.input.keyboard.on('keydown-DOWN', () => moveDown(scene));
  scene.input.keyboard.on('keydown-UP', () => rotate(scene));
  scene.input.keyboard.on('keydown-SPACE', () => dropToBottom(scene));
  scene.input.keyboard.on('keydown-P', () => togglePause(scene));
  
  // Touch controls for mobile with improved zones
  scene.input.on('pointerdown', (pointer) => {
    if (!scene.isGameActive || scene.isPaused) {
      // If game over or paused, restart on tap
      if (!scene.isGameActive) {
        restartGame(scene);
        return;
      } else if (scene.isPaused) {
        togglePause(scene);
        return;
      }
    }
    
    // Get game dimensions for better zone calculation
    const width = scene.game.config.width;
    const height = scene.game.config.height;
    
    // Normalize pointer coordinates to game scale
    const x = pointer.x;
    const y = pointer.y;
    
    // Simple touch controls in clearly defined zones
    const relX = x / width; // 0 to 1 horizontal position
    const relY = y / height; // 0 to 1 vertical position
    
    // Left third of screen - move left
    if (relX < 0.33) {
      moveLeft(scene);
      
      // Optional vibration feedback
      if (navigator.vibrate) {
        navigator.vibrate(MOBILE_CONFIG.VIBRATION_DURATION);
      }
    } 
    // Right third of screen - move right
    else if (relX > 0.66) {
      moveRight(scene);
      
      // Optional vibration feedback
      if (navigator.vibrate) {
        navigator.vibrate(MOBILE_CONFIG.VIBRATION_DURATION);
      }
    } 
    // Top third of screen - rotate
    else if (relY < 0.33) {
      rotate(scene);
      
      // Optional vibration feedback
      if (navigator.vibrate) {
        navigator.vibrate(MOBILE_CONFIG.VIBRATION_DURATION);
      }
    } 
    // Bottom third of screen - drop
    else if (relY > 0.66) {
      dropToBottom(scene);
      
      // Optional vibration feedback
      if (navigator.vibrate) {
        navigator.vibrate(MOBILE_CONFIG.VIBRATION_DURATION * 2);
      }
    } 
    // Middle area - move down
    else {
      moveDown(scene);
    }
  });
  
  // Add swipe support for better touch controls
  let touchStartX = 0;
  let touchStartY = 0;
  let touchStartTime = 0;
  
  scene.input.on('pointerdown', (pointer) => {
    touchStartX = pointer.x;
    touchStartY = pointer.y;
    touchStartTime = Date.now();
  });
  
  scene.input.on('pointerup', (pointer) => {
    if (!scene.isGameActive || scene.isPaused) return;
    
    const touchEndX = pointer.x;
    const touchEndY = pointer.y;
    const touchEndTime = Date.now();
    
    const dx = touchEndX - touchStartX;
    const dy = touchEndY - touchStartY;
    const dt = touchEndTime - touchStartTime;
    
    // Only process quick swipes (less than 300ms)
    if (dt < 300) {
      // Horizontal swipe
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 30) {
        if (dx > 0) {
          // Swipe right
          moveRight(scene);
        } else {
          // Swipe left
          moveLeft(scene);
        }
      }
      // Vertical swipe
      else if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 30) {
        if (dy > 0) {
          // Swipe down - drop
          dropToBottom(scene);
        } else {
          // Swipe up - rotate
          rotate(scene);
        }
      }
    }
  });
}

// Movement functions
export function moveLeft(scene) {
  if (!scene.isGameActive || scene.isPaused) return;
  
  // Prevent moving too fast
  const now = Date.now();
  if (now - scene.lastMoveTime < scene.moveDelay) return;
  scene.lastMoveTime = now;
  
  if (canMoveTo(scene.board, scene.currentBlock, -1, 0)) {
    scene.currentBlock.x--;
    scene.drawBlock(scene, scene.currentBlock);
  }
}

export function moveRight(scene) {
  if (!scene.isGameActive || scene.isPaused) return;
  
  // Prevent moving too fast
  const now = Date.now();
  if (now - scene.lastMoveTime < scene.moveDelay) return;
  scene.lastMoveTime = now;
  
  if (canMoveTo(scene.board, scene.currentBlock, 1, 0)) {
    scene.currentBlock.x++;
    scene.drawBlock(scene, scene.currentBlock);
  }
}

export function moveDown(scene) {
  if (!scene.isGameActive || scene.isPaused) return;
  
  if (canMoveTo(scene.board, scene.currentBlock, 0, 1)) {
    scene.currentBlock.y++;
    scene.drawBlock(scene, scene.currentBlock);
  } else {
    // Lock the block and create a new one
    scene.lockCurrentBlock();
  }
}

export function rotate(scene) {
  if (!scene.isGameActive || scene.isPaused) return;
  
  if (rotateBlock(scene.board, scene.currentBlock)) {
    scene.drawBlock(scene, scene.currentBlock);
  }
}

export function dropToBottom(scene) {
  if (!scene.isGameActive || scene.isPaused) return;
  
  // Find how far the piece can drop
  let dropDistance = 0;
  while (canMoveTo(scene.board, scene.currentBlock, 0, dropDistance + 1)) {
    dropDistance++;
  }
  
  if (dropDistance > 0) {
    scene.currentBlock.y += dropDistance;
    scene.drawBlock(scene, scene.currentBlock);
    
    // Award points for hard drop
    scene.updateScore(dropDistance);
  }
  
  // Lock it immediately
  scene.lockCurrentBlock();
}

export function togglePause(scene) {
  scene.isPaused = !scene.isPaused;
  
  if (scene.isPaused) {
    // Pause the game
    if (scene.dropTimer) scene.dropTimer.paused = true;
    scene.pauseDisplay = scene.createPauseDisplay(scene);
  } else {
    // Resume the game
    if (scene.dropTimer) scene.dropTimer.paused = false;
    if (scene.pauseDisplay) {
      scene.pauseDisplay.overlay.destroy();
      scene.pauseDisplay.pausedText.destroy();
      scene.pauseDisplay = null;
    }
  }
}

export function restartGame(scene) {
  // This function is exposed so it can be called from the scene
  scene.restartGame();
}
